import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CountryProvider } from "./contexts/CountryContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";

// Layout
import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";

import HomePage from "./pages/HomePage";
import CategoriesPage from "./pages/CategoriesPage";
import CategoryDetailPage from "./pages/CategoryDetailPage";
import ServicesPage from "./pages/ServicesPage";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import ProvidersPage from "./pages/ProvidersPage";
import ProviderDetailPage from "./pages/ProviderDetailPage";
import QuoteRequestPage from "./pages/QuoteRequestPage";
import MyQuotesPage from "./pages/MyQuotesPage";
import QuoteDetailPage from "./pages/QuoteDetailPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import JoinAsProPage from "./pages/JoinAsProPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import FAQPage from "./pages/FAQPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import CancellationPolicyPage from "./pages/CancellationPolicyPage";
import ProviderAgreementPage from "./pages/ProviderAgreementPage";

// Analytics
import { pixelPageView } from "./lib/metaPixel";

// Provider pages (already extracted)
import {
  ProviderLayout, ProviderDashboardPage, ProviderOnboardingPage,
  ProviderProfilePage, ProviderServicesPage, ProviderQuotesPage,
  ProviderDocumentsPage, ProviderSubscriptionPage, ProviderReviewsPage, CheckoutSuccessPage
} from "./components/ProviderPages";

// Admin pages (already extracted)
import {
  AdminProviderListPage, AdminProviderDetailPage, AdminQuotesPage,
  AdminCategoriesPage, AdminResetPage,
  AdminOverviewPage, AdminSubscriptionsPage, AdminReviewsPage
} from "./components/AdminPages";
import {
  AdminCustomersListPage, AdminCustomerDetailPage,
} from "./components/AdminCustomersPages";

// Recovery Mode Redirect
const RecoveryRedirect = () => {
  const { isRecoveryMode, recoveryError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if ((isRecoveryMode || recoveryError) && location.pathname !== '/reset-password') {
      navigate('/reset-password', { replace: true });
    }
  }, [isRecoveryMode, recoveryError, navigate, location.pathname]);
  return null;
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Meta Pixel — fire PageView on every SPA route change. The base script in
// public/index.html already fires the FIRST PageView. This catches subsequent
// client-side navigations (which never trigger a full reload).
const PixelRouteTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    // Skip the very first render — base snippet already fired it.
    if (window.__pixelInitialPageViewSeen) {
      pixelPageView();
    } else {
      window.__pixelInitialPageViewSeen = true;
    }
  }, [pathname]);
  return null;
};

function App() {
  return (
    <AuthProvider>
      <CountryProvider>
        <BrowserRouter>
          <div className="App">
            <ScrollToTop />
            <PixelRouteTracker />
            <RecoveryRedirect />
            <Header />
            <main className="main-content">
              <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/categories/:slug" element={<CategoryDetailPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/services/:slug" element={<ServiceDetailPage />} />
              <Route path="/providers" element={<ProvidersPage />} />
              <Route path="/providers/:id" element={<ProviderDetailPage />} />
              <Route path="/quote-request/:slug" element={<QuoteRequestPage />} />
              <Route path="/my-quotes" element={<MyQuotesPage />} />
              <Route path="/my-quotes/:quoteId" element={<QuoteDetailPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/join-as-pro" element={<JoinAsProPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/cancellation-policy" element={<CancellationPolicyPage />} />
              <Route path="/provider-agreement" element={<ProviderAgreementPage />} />
              <Route path="/provider/onboarding" element={<ProviderOnboardingPage />} />
              <Route path="/provider/checkout-success" element={<CheckoutSuccessPage />} />
              <Route path="/provider/dashboard" element={<ProviderLayout><ProviderDashboardPage /></ProviderLayout>} />
              <Route path="/provider/profile" element={<ProviderLayout><ProviderProfilePage /></ProviderLayout>} />
              <Route path="/provider/services" element={<ProviderLayout><ProviderServicesPage /></ProviderLayout>} />
              <Route path="/provider/quotes" element={<ProviderLayout><ProviderQuotesPage /></ProviderLayout>} />
              <Route path="/provider/documents" element={<ProviderLayout><ProviderDocumentsPage /></ProviderLayout>} />
              <Route path="/provider/reviews" element={<ProviderLayout><ProviderReviewsPage /></ProviderLayout>} />
              <Route path="/provider/subscription" element={<ProviderLayout><ProviderSubscriptionPage /></ProviderLayout>} />
              <Route path="/admin" element={<AdminOverviewPage />} />
              <Route path="/admin/providers" element={<AdminProviderListPage />} />
              <Route path="/admin/providers/:id" element={<AdminProviderDetailPage />} />
              <Route path="/admin/customers" element={<AdminCustomersListPage />} />
              <Route path="/admin/customers/:id" element={<AdminCustomerDetailPage />} />
              <Route path="/admin/quotes" element={<AdminQuotesPage />} />
              <Route path="/admin/subscriptions" element={<AdminSubscriptionsPage />} />
              <Route path="/admin/reviews" element={<AdminReviewsPage />} />
              <Route path="/admin/categories" element={<AdminCategoriesPage />} />
              <Route path="/admin/reset" element={<AdminResetPage />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </BrowserRouter>
      </CountryProvider>
    </AuthProvider>
  );
}

export default App;
